import './option-hooks';
import { isEqual } from 'lodash';
import { DATASET_PROP } from '../consts';
import { DatasetRequestFactory, createDatasetQueryFilterStoreable } from '../models';
import * as QueryConst from '../utils/query-const';
import { ElemDatasetBaseMixinTypes } from './types';
import { bindComponentContext, CancelToken, extractQueryModelsParams, extractQueryModelsVariables } from './utils';
import { ElemDatasetWatchStoreMixin } from './ElemDatasetWatchStoreMixin';
import { ElemDatasetPropChangeMixin } from './ElemDatasetPropChangeMixin';
import { ElemDatasetPlaceholderDefinition } from '../config';
import { DataClientRequestError, DatasetClientFactory } from '../services';
import { ELEM_DATASET_BASE_MIXIN_NAME } from './const';

/**
 *
 * @typedef {import('@goodt-common/data').DataClient} DataClient
 * @typedef {import('@goodt-common/data').Request} DatasetRequest
 * @typedef {import('@goodt-common/data').IDatasetResult} IDatasetResult
 * @typedef {import('@goodt-common/data').DataClientRequestError} DataClientRequestError
 * @typedef {import('@goodt-common/data').IDatasetRequestProps} IDatasetRequestProps
 * @typedef {import('@goodt-common/data').IDatasetEntity} IDatasetEntity
 * @typedef {import('@goodt-common/data').IDatasetVariable} IDatasetVariable
 * @typedef {import('@goodt-common/data').IDatasetVariable} IDatasetVariable
 * @typedef {import('@goodt-common/data').IQueryFilterInfo} IQueryFilterInfo
 * @typedef {import('@goodt-common/data').DatasetQueryFilterInfoStoreable} DatasetQueryFilterInfoStoreable
 */

export const ElemDatasetBaseMixin = {
    _name: ELEM_DATASET_BASE_MIXIN_NAME,
    mixins: [ElemDatasetWatchStoreMixin, ElemDatasetPropChangeMixin],
    /**
     * User-defined datasets load data lifecycle hooks
     */
    hooks: {},
    static() {
        const vm = this;
        return {
            DATASET_PROP,
            loadDataHooks: {
                // before: (cancel) => {},
                // then: (result) => {},
                // catch: (error) => {}},
                // finally: () => {},
                /**
                 * back compatibility with dremio-based widgets
                 */
                ...vm.loadDataHooks,
                /**
                 * options 'hooks' import
                 */
                ...bindComponentContext(vm.$options.hooks, vm)
            },
            ...QueryConst,
            /**
             * use one DataClient instance for widget due individual request cancel via DataClient.getNextRequestCancel() underhood
             * @type {DataClient}
             */
            client: DatasetClientFactory()
        };
    },
    data: () => ({
        /**
         * @type {boolean}
         */
        isLoading: false,
        /**
         * @type {(IDatasetResult|null)[]}
         */
        results: [],
        /**
         * @type {(DatasetRequest|null)[]}
         */
        requests: [],
        /**
         * @type {DataClientRequestError|Error|null}
         */
        error: null,
        /**
         *
         */
        ...ElemDatasetBaseMixinTypes
    }),
    computedEditor: {
        // default placeholder options for <w-elem></w-elem>
        $placeholder() {
            return {
                ...ElemDatasetPlaceholderDefinition,
                show: !this.hasDataset,
            };
        },
        hasDataset() {
            return this.datasetRequests.filter(Boolean).length > 0;
        },
        /**
         * @return {(IDatasetRequestProps|null)[]}
         */
        datasetRequests() {
            return this.props[this.DATASET_PROP] ?? [];
        },
        /**
         * @return {IDatasetEntity['name'][]}
         */
        dimensions() {
            return this.$queryGetParams(['dimensions']);
        },
        /**
         * @return {IDatasetEntity['name'][]}
         */
        metrics() {
            return this.$queryGetParams(['metrics']);
        },
        /**
         * @return {string[]}
         */
        fields() {
            return this.$queryGetParams(['fields']);
        },
        /**
         * @return {IDatasetVariable['name'][]}
         */
        variables() {
            return this.$queryGetVariables();
        }
    },

    computed: {
        /**
         * @return {boolean}
         */
        isSomeRequestLoading() {
            return this.requests
                .filter(Boolean)
                .map(({ isLoading }) => isLoading)
                .includes(true);
        }
    },

    watch: {
        datasetRequests: {
            /**
             * @param {(DatasetRequest|null)[]} newRequests
             * @param {(DatasetRequest|null)[]} [oldRequests=[]]
             */
            handler: 'onDatasetRequestsChanged',
            immediate: true
        },
        requests: {
            /**
             *
             * @param {(DatasetRequest|null)[]} newRequests
             * @param {(DatasetRequest|null)[]} [oldRequests=[]]
             */
            handler(newRequests, oldRequests = []) {
                // eslint-disable-next-line no-restricted-syntax
                const changedRequests = newRequests.filter(
                    (newRequest, index) => newRequest != null && newRequest !== oldRequests[index]
                );
                this.$requestChanged(changedRequests);
            }
        },
        isSomeRequestLoading: {
            /**
             * @param {boolean} isLoading
             */
            handler(isLoading) {
                this.isLoading = isLoading;
            }
        }
    },

    destroyed() {
        this.$requestCancel();
    },

    methods: {
        /**
         * Updates this.requests with new Request instances when this.props[this.DATASET_PROP] changed
         * @param {(IDatasetRequestProps|null)[]} [newDatasetRequests]
         * @param {(IDatasetRequestProps|null)[]} [oldDatasetRequests=[]]
         */
        onDatasetRequestsChanged(newDatasetRequests, oldDatasetRequests = []) {
            let isChanged = newDatasetRequests.length !== oldDatasetRequests.length;
            // вариант с reduce плохо читается, поэтому используем map + сайд-эффект
            const requests = newDatasetRequests.map((requestProps, index) => {
                // if request not changed reuse from this.requests
                // make sense for multiple dataset requests list
                if (isEqual(requestProps, oldDatasetRequests[index])) {
                    return this.requests[index];
                }
                isChanged = true;
                if (requestProps == null) {
                    return null;
                }
                return this.$requestCreate(requestProps);
            });
            if (isChanged) {
                this.requests = requests;
            }
        },
        /**
         * Create request
         * @param {IDatasetRequestProps} requestProps
         */
        $requestCreate(requestProps) {
            return DatasetRequestFactory(requestProps, { client: this.client });
        },
        /**
         *
         * @param {(DatasetRequest|null)[]} requests
         */
        $requestChanged(requests) {
            // eslint-disable-next-line no-param-reassign
            requests = [requests].flat();
            const [shouldLoad, orderedRequests] = this.requests.reduce(
                ([_shouldLoad, _orderedRequests], request) => {
                    const isChanged = request != null && requests.includes(request);
                    // prettier-ignore
                    return [
                            _shouldLoad || isChanged,
                            [..._orderedRequests, isChanged ? request : null]
                        ];
                },
                [false, []]
            );
            if (shouldLoad) {
                orderedRequests.forEach((request) => {
                    if (request != null) {
                        request.offset = 0;
                        request.cancel();
                    }
                });
                this.loadData(orderedRequests);
            }
        },
        /**
         * @public
         * @param {(DatasetRequest|null)[]} requests
         */
        async loadData(requests = this.requests) {
            const { loadDataHooks } = this;
            await this.$requestFetch(requests, {
                then: (results) => {
                    // обновить результаты только тех запросов, которые не null
                    this.results = results.map((result, i) => result ?? this.results?.[i] ?? null);
                    loadDataHooks.then?.(this.results);
                },
                catch: (error) => {
                    this.results = [];
                    loadDataHooks.catch?.(error);
                }
            });

            /*
            if (requests.filter(Boolean).length === 0) {
                return;
            }

            this.$requestCancel(requests);

            if (this.loadDataHooks.before != null) {
                const cancelToken = new CancelToken();
                this.loadDataHooks.before.call(this, cancelToken.cancel.bind(cancelToken));
                if (cancelToken.isCancelled === true) {
                    return;
                }
            }

            try {
                const results = await Promise.all(requests.map((request) => request?.send() ?? null));
                // обновить результаты только тех запросов, которые не null
                this.results = results.map((result, i) => result ?? this.results?.[i] ?? null); // ?? []
                this.loadDataHooks.then?.call(this, this.results);
            } catch (error) {
                this.results = [];
                this.$handleError(error, {
                    scope: {
                        method: 'loadData'
                    }
                });
                this.loadDataHooks.catch?.call(this, error);
            } finally {
                this.loadDataHooks.finally?.call(this);
            }
            */
        },

        /**
         * @public
         * @param {(DatasetRequest|null)[]} requests
         * @param {{ before?: function, then?: function, catch?: function, finally?: function }} hooks
         */
        async $requestFetch(requests, hooks) {
            if (requests.filter(Boolean).length === 0) {
                return;
            }

            this.$requestCancel(requests);

            const { loadDataHooks } = this;
            const { before: beforeHook = loadDataHooks.before } = hooks;
            if (beforeHook != null) {
                const cancelToken = new CancelToken();
                beforeHook(cancelToken.cancel.bind(cancelToken));
                if (cancelToken.isCancelled === true) {
                    return;
                }
            }

            const {
                then: thenHook = loadDataHooks.then,
                catch: catchHook = loadDataHooks.catch,
                finally: finallyHook = loadDataHooks.finally
            } = hooks;
            try {
                const results = await Promise.all(requests.map((request) => request?.send() ?? null));
                thenHook?.(results);
            } catch (error) {
                this.$handleError(error, {
                    scope: {
                        method: 'loadData'
                    }
                });
                catchHook?.(error);
            } finally {
                finallyHook?.();
            }
        },

        /**
         * @deprecated use this.$requestCancel instead
         * @param {(DatasetRequest|null)[]} [requests]
         */
        cancelRequests(requests) {
            this.$requestCancel(requests);
        },
        /**
         *
         * @param {(DatasetRequest|null|DatasetRequest|null)[]} [requests]
         */
        $requestCancel(requests = this.requests) {
            [requests]
                .flat()
                .filter(Boolean)
                .forEach((request) => request.cancel());
        },
        /**
         * Extended ElemBase.$handleError dataset BL specific behaviour
         * @param {Error} error
         * @param {any} scope
         */
        $handleError(error, { scope } = {}) {
            if (error instanceof DataClientRequestError) {
                error.context = this;
            }
            this.super().$handleError.call(this, error, { scope });
            if (error instanceof DataClientRequestError === false) {
                return;
            }
            if (this.isEditorMode === false) {
                return;
            }
            this.error = error;
        },
        /**
         *
         * @param {string[]} params
         * @param {(DatasetRequest|null)[]} [requests]
         * @return {string[]}
         */
        $queryGetParams(params, requests = this.requests) {
            return extractQueryModelsParams(
                [requests]
                    .flat()
                    .filter(Boolean)
                    .map(({ query }) => query),
                params
            );
        },
        /**
         *
         * @param {(DatasetRequest|null)[]} [requests]
         * @return {string[]}
         */
        $queryGetVariables(requests = this.requests) {
            return extractQueryModelsVariables(
                [requests]
                    .flat()
                    .filter(Boolean)
                    .map(({ query }) => query)
            );
        },
        /**
         *
         * @param {string} param
         * @param {(DatasetRequest|null)[]} [requests]
         * @return {null|string}
         */
        $queryGetParamName(param, requests = this.requests) {
            const request = [requests]
                .flat()
                .filter(Boolean)
                .find(({ query }) => query.getParamName(param) != null);
            return request?.query.getParamName(param) ?? null;
        },
        /**
         *
         * @param {string} param
         * @param {(DatasetRequest|null)[]} [requests]
         * @return {boolean}
         */
        $queryHasFilter(param, requests = this.requests) {
            return [requests]
                .flat()
                .filter(Boolean)
                .some(({ query }) => query.hasFilter(param));
        },
        /**
         * @param {IQueryFilterInfo} options
         * @return {DatasetQueryFilterInfoStoreable}
         */
        $queryCreateStoreFilter(options) {
            return createDatasetQueryFilterStoreable(options);
        }
    }
};
